import { ChevronRight } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import type {
  PackagingItem,
  PackagingSupplierPackagedItem,
} from '@/shared/hooks/api/packagingSuppliers'
import { Button } from '@/components/ui/button'
import { LexAddPackagingToPartMenu } from './LexAddPackagingToPartMenu'
import {
  Empty,
  EmptyHeader,
  EmptyTitle,
} from '@/components/ui/empty'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import type { AddPackagingItemTarget } from './SupplierDetailPackagingCatalog'
import { PackedItemSkuCell } from './PackedItemSkuCell'

type SupplierPartPackagingTableProps = {
  packagingSupplierId: string
  packagedItems: PackagingSupplierPackagedItem[]
  packagingItems: PackagingItem[]
  canWriteOrgData: boolean
  hasSupplierAddress: boolean
  onAddItem: (target: Extract<AddPackagingItemTarget, { type: 'packedItem' }>) => void
}

export function SupplierPartPackagingTable({
  packagingSupplierId,
  packagedItems,
  packagingItems,
  canWriteOrgData,
  hasSupplierAddress,
  onAddItem,
}: SupplierPartPackagingTableProps) {
  const { t } = useTranslation('packagingSuppliers')
  const columnCount = canWriteOrgData ? 4 : 3

  return (
    <div className='overflow-x-auto rounded-md border'>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>{t('details.partPackaging.columnName')}</TableHead>
            <TableHead>{t('packedItems.columnSku')}</TableHead>
            <TableHead className='text-end'>
              {t('details.partPackaging.columnPackagingItemCount')}
            </TableHead>
            {canWriteOrgData && <TableHead />}
          </TableRow>
        </TableHeader>
        <TableBody>
          {packagedItems.length > 0 ? (
            packagedItems.map((packedItem) => (
              <TableRow key={packedItem.id}>
                <TableCell>
                  <Link
                    to={`/packaging/packed-items/${packedItem.id}`}
                    className='font-medium text-foreground underline decoration-dotted underline-offset-2'
                  >
                    {packedItem.name}
                  </Link>
                </TableCell>
                <TableCell>
                  <PackedItemSkuCell
                    sku={packedItem.sku}
                    notInSourceList={packedItem.notInSourceList ?? false}
                    fromPartsCatalog={packedItem.fromPartsCatalog ?? false}
                  />
                </TableCell>
                <TableCell className='text-end tabular-nums'>
                  {packedItem.packagingItems.length}
                </TableCell>
                {canWriteOrgData && (
                  <TableCell className='text-end'>
                    <div className='flex justify-end gap-1'>
                      <LexAddPackagingToPartMenu
                        supplierId={packagingSupplierId}
                        packagedItemId={packedItem.id}
                        packedItemSku={packedItem.sku}
                        assignedPackagingItemIds={
                          new Set(
                            packedItem.packagingItems.map(
                              (packagingItem) => packagingItem.id
                            )
                          )
                        }
                        packagingItems={packagingItems}
                        hasSupplierAddress={hasSupplierAddress}
                        onAddItem={onAddItem}
                      />
                      <Button variant='ghost' size='icon-sm' asChild>
                        <Link
                          to={`/packaging/packed-items/${packedItem.id}`}
                          aria-label={t('details.partPackaging.viewPart', {
                            name: packedItem.name,
                          })}
                        >
                          <ChevronRight className='size-4' />
                        </Link>
                      </Button>
                    </div>
                  </TableCell>
                )}
              </TableRow>
            ))
          ) : (
            <TableRow>
              <TableCell colSpan={columnCount} className='p-0'>
                <Empty className='rounded-none border-0 py-10'>
                  <EmptyHeader>
                    <EmptyTitle>{t('details.partPackaging.empty')}</EmptyTitle>
                  </EmptyHeader>
                </Empty>
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </div>
  )
}
